// DF HACCP SafeGuard — registrazioni senza connessione e sincronizzazione automatica.
//
// Come funziona:
// - ogni registrazione parte con un `client_record_id` generato qui: se la rete cade dopo il
//   salvataggio, il reinvio restituisce la stessa registrazione (nessun doppione sul server);
// - se il server non è raggiungibile (rete assente, 502/503/504) la registrazione resta in coda
//   su questo dispositivo con ora, giorno e operatore originali;
// - appena la rete torna la coda viene inviata in ordine; un elemento si considera concluso solo
//   quando il server conferma il salvataggio. Conflitti (controllo già registrato da un altro
//   dispositivo) e rifiuti restano visibili e non vengono mai cancellati in automatico;
// - per compilare offline le schermate dei controlli usano le ultime risposte salvate.
// Limiti reali: offline continua solo l'utente già collegato su questo dispositivo; il browser può
// cancellare i dati locali (spazio, impostazioni, iPhone se l'app non è nella schermata Home).
import { toast } from "sonner";
import { romeDateKey } from "./dayRollover";
import { allItems, clearStore, deleteItem, getItem, putItem } from "./offlineStore";

const REG_PATH = "/app/registrations";
const CACHEABLE = [/^\/app\/context$/, /^\/app\/procedures\/today/, /^\/app\/equipment$/, /^\/app\/controls\/calendar/,
  /^\/app\/controls\/missing-days/, /^\/app\/safeguard\/status$/, /^\/app\/safeguard\/documents$/];
const KEEP_SYNCED_DAYS = 7;
// Offline il profilo resta valido 24 ore dall'ultima verifica col server: poi serve un nuovo accesso
// online (le registrazioni in coda restano qui e partono dopo l'accesso).
const OFFLINE_SESSION_HOURS = 24;
const OPEN = ["in_attesa", "in_invio"];

let api = null;
let currentUser = null;
let syncing = false;
const listeners = new Set();

export const newRecordId = () => (window.crypto?.randomUUID ? window.crypto.randomUUID()
  : `${Date.now().toString(16)}-${Math.random().toString(16).slice(2, 10)}-${Math.random().toString(16).slice(2, 10)}`);

export const isOfflineError = (error) => !error?.response || [502, 503, 504].includes(error.response.status);
// Da ritentare (mai scartare): server irraggiungibile, errori interni temporanei, troppe richieste, timeout.
const isRetryable = (error) => isOfflineError(error) || error.response.status >= 500 || [408, 429].includes(error.response.status);

const pathOf = (config) => (config?.url || "").split("?")[0];
const cacheKey = (config) => {
  const [path, query = ""] = (config.url || "").split("?");
  const params = new URLSearchParams(query);
  Object.entries(config.params || {}).forEach(([k, v]) => { if (v !== undefined && v !== null) params.set(k, String(v)); });
  const sorted = [...params.entries()].sort(([a], [b]) => a.localeCompare(b)).map(([k, v]) => `${k}=${v}`).join("&");
  return `${currentUser?.id || "anon"}|${path}?${sorted}`;
};

// ---------------------------------------------------------------- stato e notifiche alla UI

// Elementi della coda visibili: solo quelli dell'azienda indicata (o di quella dell'utente collegato).
// Senza un'azienda nota non si mostra nulla: la coda di altri non deve comparire.
export async function queueItems(tenantId = currentUser?.tenant_id) {
  if (!tenantId) return [];
  try {
    return (await allItems("queue")).filter((i) => i.tenant_id === tenantId).sort((a, b) => a.created_at.localeCompare(b.created_at));
  } catch { return []; }
}

export async function queueSummary(tenantId) {
  const items = await queueItems(tenantId);
  return {
    items,
    pending: items.filter((i) => OPEN.includes(i.status)).length,
    conflicts: items.filter((i) => i.status === "conflitto").length,
    rejected: items.filter((i) => i.status === "rifiutata").length,
    synced: items.filter((i) => i.status === "sincronizzata").length,
    syncing,
  };
}

async function notify() {
  const summary = await queueSummary();
  listeners.forEach((fn) => fn(summary));
}

export function subscribeQueue(fn, tenantId) {
  const listener = (summary) => (tenantId && tenantId !== currentUser?.tenant_id ? queueSummary(tenantId).then(fn) : fn(summary));
  listeners.add(listener);
  queueSummary(tenantId).then(fn);
  return () => listeners.delete(listener);
}

// ---------------------------------------------------------------- utente e sessione offline

export async function rememberUser(user) {
  currentUser = user || null;
  notify();
  if (!user || user.impersonated_by) return;
  const { signature, device_ids, ...minimal } = user; // eslint-disable-line no-unused-vars
  try { await putItem("meta", { key: "user", user: minimal, saved_at: Date.now() }); } catch { /* IndexedDB non disponibile */ }
}

export async function offlineUser() {
  try {
    const saved = await getItem("meta", "user");
    if (!saved || Date.now() - saved.saved_at > OFFLINE_SESSION_HOURS * 3600000) return null;
    currentUser = saved.user;
    notify();
    return { ...saved.user, offline: true };
  } catch {
    return null;
  }
}

// Uscita esplicita: si tolgono profilo e schermate salvate, MAI le registrazioni non ancora confermate.
export async function forgetUser() {
  currentUser = null;
  notify();
  try { await deleteItem("meta", "user"); await clearStore("cache"); } catch { /* niente da cancellare */ }
}

// ---------------------------------------------------------------- schermate salvate

async function overlayPending(data) {
  // Le registrazioni in coda contano come fatte: evita di ripetere offline lo stesso controllo.
  if (!data?.procedures) return data;
  const pending = (await queueItems()).filter((i) => OPEN.includes(i.status) && i.user_id === currentUser?.id
    && i.body.reference_date === data.date);
  if (!pending.length) return data;
  const ids = new Set(pending.map((i) => i.body.procedure_id));
  return {
    ...data,
    procedures: data.procedures.map((p) => (ids.has(p.id) && p.period !== "on_demand"
      ? { ...p, completed: true, offline_pending: true, registration_status: p.registration_status || "in_attesa_sincronizzazione" }
      : p)),
  };
}

async function cachedResponse(config) {
  const path = pathOf(config);
  let saved = null;
  try { saved = await getItem("cache", cacheKey(config)); } catch { saved = null; }
  let data = saved?.data;
  if (!saved && path === "/app/procedures/today") {
    // Giornata mai aperta su questo dispositivo: si riparte dall'ultimo elenco salvato, azzerando gli esiti.
    const wanted = new URLSearchParams((config.url || "").split("?")[1] || "").get("date_key") || config.params?.date_key || romeDateKey();
    const target = stripDate(cacheKey(config));
    const candidates = (await allItems("cache").catch(() => [])).filter((c) => stripDate(c.key) === target);
    const latest = candidates.sort((a, b) => b.saved_at - a.saved_at)[0];
    if (latest) {
      data = { ...latest.data, date: wanted, offline_copy_of: latest.data.date,
        procedures: (latest.data.procedures || []).map((p) => ({ ...p, completed: false, registration_status: null, offline_pending: false })) };
      saved = latest;
    }
  }
  if (!saved && /^\/app\/controls\//.test(path)) {
    const latest = (await allItems("cache").catch(() => [])).filter((c) => c.key.startsWith(`${currentUser?.id}|${path}`)).sort((a, b) => b.saved_at - a.saved_at)[0];
    if (latest) { data = latest.data; saved = latest; }
  }
  if (!saved) return null;
  if (path === "/app/procedures/today") data = await overlayPending(data);
  return { data, status: 200, statusText: "OK (copia offline)", headers: { "x-offline-copy": String(saved.saved_at) }, config, offline: true };
}

function stripDate(key) {
  const [user, rest = ""] = key.split("|");
  const [path, query = ""] = rest.split("?");
  const params = new URLSearchParams(query);
  params.delete("date_key");
  return `${user}|${path}?${[...params.entries()].sort(([a], [b]) => a.localeCompare(b)).map(([k, v]) => `${k}=${v}`).join("&")}`;
}

// ---------------------------------------------------------------- coda

async function procedureName(procedureId) {
  const cached = await allItems("cache").catch(() => []);
  for (const entry of cached) {
    const found = (entry.data?.procedures || []).find((p) => p.id === procedureId);
    if (found) return found.name;
  }
  return null;
}

async function enqueue(config) {
  const body = typeof config.data === "string" ? JSON.parse(config.data) : { ...(config.data || {}) };
  const item = {
    client_record_id: body.client_record_id,
    body: { ...body, online: false, reference_date: body.reference_date || romeDateKey(), device_time: body.device_time || new Date().toISOString() },
    user_id: currentUser?.id, user_name: currentUser?.name, tenant_id: currentUser?.tenant_id,
    procedure_name: await procedureName(body.procedure_id),
    created_at: new Date().toISOString(), status: "in_attesa", attempts: 0, last_error: null,
  };
  await putItem("queue", item);
  try { if (navigator.storage?.persist) navigator.storage.persist(); } catch { /* non supportato */ }
  toast.info("Nessuna connessione: registrazione salvata su questo dispositivo. Verrà inviata automaticamente appena torna la rete.", { id: "offline-saved" });
  notify();
  return item;
}

// Una sola scheda alla volta invia la coda (Web Locks dove disponibile).
export function syncNow() {
  if (navigator.locks?.request) return navigator.locks.request("df-haccp-offline-sync", { ifAvailable: true }, (lock) => (lock ? runSync() : null));
  return runSync();
}

async function runSync() {
  if (syncing || !api || !navigator.onLine || !currentUser) return;
  const items = (await queueItems()).filter((i) => OPEN.includes(i.status) && i.user_id === currentUser.id);
  if (!items.length) return;
  syncing = true;
  notify();
  try {
    // La sessione nel browser deve essere ancora di chi ha fatto le registrazioni (un'altra scheda può aver cambiato utente).
    try {
      const { data } = await api.get("/auth/me", { _offlineSync: true });
      if (data.user?.id !== currentUser.id || data.user?.tenant_id !== currentUser.tenant_id) return;
    } catch { return; }
    for (const item of items) {
      await putItem("queue", { ...item, status: "in_invio", last_attempt_at: new Date().toISOString() });
      try {
        const { data } = await api.post(REG_PATH, item.body, { _offlineSync: true });
        await putItem("queue", { ...item, status: "sincronizzata", attempts: item.attempts + 1, synced_at: new Date().toISOString(),
          server_id: data.registration?.id, server_time: data.registration?.server_time,
          server_status: data.registration?.status, last_error: null });
      } catch (error) {
        const detail = error.response?.data?.detail;
        const message = typeof detail === "string" ? detail : detail?.msg || "Errore";
        if (isRetryable(error) || error.response?.status === 401) {
          await putItem("queue", { ...item, status: "in_attesa", attempts: item.attempts + 1,
            last_error: error.response?.status === 401 ? "Accedi di nuovo per inviare le registrazioni"
              : isOfflineError(error) ? "Server non raggiungibile" : "Errore temporaneo del server: nuovo tentativo automatico" });
          break;
        }
        const status = error.response?.status === 409 ? "conflitto" : "rifiutata";
        await putItem("queue", { ...item, status, attempts: item.attempts + 1, last_error: message,
          conflict_id: detail?.conflict_id, resolved_at: new Date().toISOString() });
        toast.error(status === "conflitto"
          ? `Registrazione offline in conflitto: ${message}`
          : `Registrazione offline non accettata dal server: ${message}`, { duration: 8000 });
      }
    }
    // Le registrazioni confermate dal server si tolgono dal dispositivo dopo qualche giorno.
    const limit = Date.now() - KEEP_SYNCED_DAYS * 86400000;
    for (const item of await queueItems(currentUser.tenant_id)) {
      if (item.status === "sincronizzata" && Date.parse(item.synced_at) < limit) await deleteItem("queue", item.client_record_id);
    }
  } finally {
    syncing = false;
    notify();
  }
}

// La persona ha letto il conflitto o il rifiuto: l'elemento resta sul server (conflitti) o va rifatto.
export async function dismissItem(id) {
  const item = await getItem("queue", id);
  if (item && !OPEN.includes(item.status)) await deleteItem("queue", id);
  notify();
}

// ---------------------------------------------------------------- integrazione con axios

export function installOffline(instance) {
  api = instance;
  instance.interceptors.request.use((config) => {
    if (config.method === "post" && pathOf(config) === REG_PATH && config.data && typeof config.data === "object" && !config.data.client_record_id) {
      config.data = { ...config.data, client_record_id: newRecordId() };
    }
    return config;
  });
  instance.interceptors.response.use(async (response) => {
    const { config } = response;
    const path = pathOf(config);
    if (config.method === "get" && currentUser && CACHEABLE.some((re) => re.test(path))) {
      putItem("cache", { key: cacheKey(config), data: response.data, saved_at: Date.now() }).catch(() => {});
      if (path === "/app/procedures/today") response.data = await overlayPending(response.data);
    }
    return response;
  }, async (error) => {
    const { config } = error;
    if (!config || config._offlineSync || !isOfflineError(error)) throw error;
    const path = pathOf(config);
    if (config.method === "post" && path === REG_PATH && currentUser) {
      const item = await enqueue(config);
      return { data: { registration: { id: item.client_record_id, status: null, offline: true }, offline: true }, status: 202,
        statusText: "Salvata sul dispositivo", headers: {}, config };
    }
    if (config.method === "get" && currentUser && CACHEABLE.some((re) => re.test(path))) {
      const cached = await cachedResponse(config);
      if (cached) return cached;
    }
    throw error;
  });

  window.addEventListener("online", () => { syncNow(); });
  document.addEventListener("visibilitychange", () => { if (document.visibilityState === "visible") syncNow(); });
  setInterval(() => { syncNow(); }, 30000);
}

// Prepara le schermate dei controlli di oggi per un eventuale uso senza rete.
export async function warmOfflineCache(ctx) {
  if (!api || !navigator.onLine || !currentUser || currentUser.impersonated_by) return;
  try {
    const last = Number(localStorage.getItem("df-offline-warm") || 0);
    if (Date.now() - last < 30 * 60 * 1000) return;
    localStorage.setItem("df-offline-warm", String(Date.now()));
  } catch { /* storage non disponibile */ }
  const day = romeDateKey();
  const requests = [api.get("/app/equipment"), api.get(`/app/procedures/today?date_key=${day}`),
    api.get(`/app/procedures/today?date_key=${day}&category=cleaning`), api.get(`/app/procedures/today?date_key=${day}&category=other`)];
  (ctx?.sites || []).forEach((s) => requests.push(api.get(`/app/procedures/today?site_id=${s.id}&date_key=${day}`)));
  await Promise.allSettled(requests);
}
